import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import { FaLock, FaSearch, FaTimes } from 'react-icons/fa';
import { HiEye } from 'react-icons/hi';

import { Button, Text, toasts, ModalMethods } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';
import { InputField } from '../../../../components/FormComponents';

import piece from '../../../../assets/icons/Stack.png';
import wear from '../../../../assets/icons/Tag.png';
import iconPlaceholder from '../../../../assets/images/image 354.png';
import IconSelectorModal, { type IconType } from './IconSelectorModal';
import CategorySelectorModal from './CategorySelectorModal';
import ReadymadeCategoryModal, { type ReadymadeCategory } from './ReadymadeCategoryModal';
import ReadymadeTagsModal, { type ReadymadeTag } from './ReadymadeTagsModal';
import ReadymadeUnitsModal, { type ReadymadeUnit } from './ReadymadeUnitsModal';

type ImageObjType = {
  reference_id: string;
  short_lived_url: string;
  url?: string;
};

type FormData = {
  outfit_name: string;
  category: string;
  gender: 'Male' | 'Female';
  icon: ImageObjType | null;
  reference_id?: string;
};

type ReadymadeGender = 'MALE' | 'FEMALE' | 'NA';

type ReadymadeFormData = {
  name: string;
  display_name: string;
  gender: ReadymadeGender;
  category: ReadymadeCategory | null;
  tags: ReadymadeTag[];
  unit: ReadymadeUnit | null;
  icon: ImageObjType | null;
};

type EditItemState = {
  id: number;
  boutique_id?: number;
  outfit_name?: string;
  name?: string;
  display_name?: string;
  category?: string | null;
  category_id?: number;
  reference_id?: string | null;
  icon_url?: string;
  image_url?: string;
  icon?: string;
  outfit_pieces?: {
    id: number;
    name: string;
    boutique_id?: number;
    gender?: string;
    icon_reference_id?: string;
    custom_outfit_id?: number;
  }[];
  gender?: string;
  tags?: { id: number; name: string; boutique_id?: number }[];
  unit?: { id: number; name: string; boutique_id?: number } | null;
  cover_image_url?: string | null;
  cover_image_ref_id?: string | null;
  order_type_id?: number;
};

const AddItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const iconSelectorModalRef = useRef<ModalMethods>(null);
  const categorySelectorModalRef = useRef<ModalMethods>(null);
  const readymadeCategoryModalRef = useRef<ModalMethods>(null);
  const readymadeTagsModalRef = useRef<ModalMethods>(null);
  const readymadeUnitsModalRef = useRef<ModalMethods>(null);

  const { editItem, mode, orderTypeId } = (location.state || {}) as {
    editItem?: EditItemState;
    mode?: 'readymade';
    orderTypeId?: number;
  };

  const isReadymadeMode = mode === 'readymade';

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    outfit_name: '',
    category: '',
    gender: 'Female',
    icon: null,
  });

  const [readymadeForm, setReadymadeForm] = useState<ReadymadeFormData>({
    name: '',
    display_name: '',
    gender: 'MALE',
    category: null,
    tags: [],
    unit: null,
    icon: null,
  });

  useEffect(() => {
    if (!editItem || isReadymadeMode) {
      return;
    }

    const outfitName = editItem.outfit_name || editItem.name || '';
    const category = editItem.category || '';
    const iconUrl = editItem.icon_url || editItem.image_url || editItem.icon || '';
    const referenceId = editItem.reference_id || undefined;

    setFormData({
      outfit_name: outfitName,
      category,
      gender: (editItem.gender as 'Male' | 'Female') || 'Female',
      icon:
        referenceId && iconUrl
          ? {
              reference_id: String(referenceId),
              short_lived_url: iconUrl,
              url: iconUrl,
            }
          : null,
    });
  }, [editItem, isReadymadeMode]);

  useEffect(() => {
    if (!editItem || !isReadymadeMode) {
      return;
    }

    const genderMap: Record<string, ReadymadeGender> = {
      MALE: 'MALE',
      FEMALE: 'FEMALE',
      Male: 'MALE',
      Female: 'FEMALE',
      NA: 'NA',
    };

    setReadymadeForm({
      name: editItem.name || '',
      display_name: editItem.display_name || editItem.name || '',
      gender: genderMap[editItem.gender || ''] || 'NA',
      category:
        editItem.category_id != null
          ? { id: editItem.category_id, name: editItem.category || '' }
          : null,
      tags: (editItem.tags || []).map((t) => ({ id: t.id, name: t.name })),
      unit: editItem.unit ? { id: editItem.unit.id, name: editItem.unit.name } : null,
      icon:
        editItem.cover_image_ref_id && editItem.cover_image_url
          ? {
              reference_id: editItem.cover_image_ref_id,
              short_lived_url: editItem.cover_image_url,
              url: editItem.cover_image_url,
            }
          : null,
    });
  }, [editItem, isReadymadeMode]);

  const handleInputChange = (value: string, key: keyof FormData) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleGenderChange = (gender: 'Male' | 'Female') => {
    setFormData((prev) => ({ ...prev, gender }));
  };

  const handleDeleteIcon = () => {
    setFormData((prev) => ({ ...prev, icon: null }));
  };

  const handleOpenIconSelector = () => {
    iconSelectorModalRef.current?.show();
  };

  const handleOpenCategorySelector = () => {
    categorySelectorModalRef.current?.show();
  };

  const handleCategorySelect = (categoryName: string) => {
    handleInputChange(categoryName, 'category');
  };

  const handleIconSelect = (icon: IconType) => {
    // Convert the icon from API response to ImageObjType format
    const iconData: ImageObjType = {
      reference_id: icon.reference_id || icon.id || icon.icon_id || '',
      short_lived_url: icon.short_lived_url || icon.icon_url || icon.url || '',
      url: icon.url || icon.icon_url || icon.short_lived_url || '',
    };

    setFormData((prev) => ({ ...prev, icon: iconData }));
  };

  const handleSaveCustomOutfit = async () => {
    const boutique_id = getValueFromLocalStorage('boutique_id');

    if (!boutique_id) {
      toasts('error', 'Boutique ID not found', 'boutique-id-missing');

      return;
    }

    if (_isEmpty(formData.outfit_name.trim())) {
      toasts('error', 'Item name is required', 'item-name-required');

      return;
    }

    if (_isEmpty(formData.category.trim())) {
      toasts('error', 'Category is required', 'category-required');

      return;
    }

    setIsLoading(true);

    try {
      const boutiqueIdNum = Number(boutique_id);

      if (editItem) {
        const updatePayload = {
          outfit_name: formData.outfit_name.trim(),
          gender: formData.gender,
          boutique_id: boutiqueIdNum,
          category: formData.category.trim(),
          atomic_outfit_details:
            editItem.outfit_pieces?.map((p) => ({
              id: p.id,
              name: p.name,
              boutique_id: p.boutique_id ?? boutiqueIdNum,
              gender: p.gender ?? formData.gender,
              icon_reference_id: p.icon_reference_id ?? '',
              custom_outfit_id: p.custom_outfit_id ?? editItem.id,
            })) ?? [],
          custom_outfit_type: 'CUSTOM',
          ...(formData.icon?.reference_id && {
            reference_id: formData.icon.reference_id,
          }),
        };
        const response = await api.putRequest(`custom_outfit/update/${editItem.id}`, updatePayload);

        if (response.status) {
          toasts('success', 'Outfit updated successfully', 'outfit-update-success');
          navigate('/settings/items-and-categories');
        } else {
          toasts('error', response.message || 'Failed to update outfit', 'outfit-update-error');
        }
      } else {
        const createPayload = {
          outfit_name: formData.outfit_name.trim(),
          gender: formData.gender,
          boutique_id: boutiqueIdNum,
          atomic_outfit_details: [],
          category: formData.category.trim(),
          custom_outfit_type: 'CUSTOM',
          ...(formData.icon?.reference_id && {
            reference_id: formData.icon.reference_id,
          }),
          ...(orderTypeId != null && { order_type_id: orderTypeId }),
        };
        const response = await api.postRequest('custom_outfit/save', createPayload);

        if (response.status) {
          toasts('success', 'Item saved successfully', 'item-saved-success');
          navigate('/settings/items-and-categories');
        } else {
          toasts('error', response.message || 'Failed to save item', 'item-save-error');
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts('error', err.message, editItem ? 'outfit-update-error' : 'item-save-error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReadymadeInputChange = <K extends keyof ReadymadeFormData>(
    key: K,
    value: ReadymadeFormData[K]
  ) => {
    setReadymadeForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleReadymadeGenderChange = (gender: ReadymadeGender) => {
    setReadymadeForm((prev) => ({ ...prev, gender }));
  };

  const handleReadymadeIconDelete = () => {
    setReadymadeForm((prev) => ({ ...prev, icon: null }));
  };

  const handleReadymadeIconSelect = (icon: IconType) => {
    const iconData: ImageObjType = {
      reference_id: icon.reference_id || icon.id || icon.icon_id || '',
      short_lived_url: icon.short_lived_url || icon.icon_url || icon.url || '',
      url: icon.url || icon.icon_url || icon.short_lived_url || '',
    };

    setReadymadeForm((prev) => ({ ...prev, icon: iconData }));
  };

  const handleReadymadeCategorySelect = (category: ReadymadeCategory | null) => {
    setReadymadeForm((prev) => ({ ...prev, category }));
  };

  const handleReadymadeTagsChange = (tags: ReadymadeTag[]) => {
    setReadymadeForm((prev) => ({ ...prev, tags }));
  };

  const handleReadymadeUnitSelect = (unit: ReadymadeUnit | null) => {
    setReadymadeForm((prev) => ({ ...prev, unit }));
  };

  const handleSaveReadymade = async () => {
    const boutique_id = getValueFromLocalStorage('boutique_id');

    if (!boutique_id) {
      toasts('error', 'Boutique ID not found', 'boutique-id-missing');

      return;
    }

    const trimmedName = readymadeForm.name.trim();

    if (_isEmpty(trimmedName)) {
      toasts('error', 'Item name is required', 'readymade-item-name-required');

      return;
    }

    if (!readymadeForm.category) {
      toasts('error', 'Category is required', 'readymade-category-required');

      return;
    }

    if (!readymadeForm.unit) {
      toasts('error', 'Unit is required', 'readymade-unit-required');

      return;
    }

    if (!orderTypeId) {
      toasts('error', 'Order type is missing', 'readymade-order-type-missing');

      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        name: trimmedName || readymadeForm.display_name.trim(),
        display_name: readymadeForm.display_name.trim() || trimmedName,
        gender: readymadeForm.gender,
        category_id: readymadeForm.category.id,
        tag_ids: readymadeForm.tags.map((tag) => tag.id),
        unit_id: readymadeForm.unit.id,
        cover_image_ref_id: readymadeForm.icon?.reference_id || null,
        image_ref_id: readymadeForm.icon?.reference_id ? [readymadeForm.icon.reference_id] : [],
        order_type_id: orderTypeId,
      };

      const response = editItem
        ? await api.putRequest(`readymade/item/initial/${editItem.id}`, payload)
        : await api.postRequest('readymade/item/initial', payload);

      if (response.status) {
        toasts(
          'success',
          editItem ? 'Item updated successfully' : 'Item saved successfully',
          editItem ? 'readymade-item-updated-success' : 'readymade-item-saved-success'
        );
        navigate('/settings/items-and-categories');
      } else {
        toasts(
          'error',
          response.message ||
            (editItem ? 'Failed to update item' : 'Failed to save readymade item'),
          editItem ? 'readymade-item-update-error' : 'readymade-item-save-error'
        );
      }
    } catch (err) {
      if (err instanceof Error) {
        toasts(
          'error',
          err.message,
          editItem ? 'readymade-item-update-error' : 'readymade-item-save-error'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const previewTitle = useMemo(() => {
    if (isReadymadeMode) {
      if (_isEmpty(readymadeForm.name.trim())) {
        return 'Kurta Pajama - Look Superhsh cool with ethnic wear';
      }

      return readymadeForm.name.trim();
    }

    if (_isEmpty(formData.outfit_name.trim())) {
      return 'Kurta Pajama - Look Superhsh cool with ethnic wear';
    }

    return formData.outfit_name.trim();
  }, [formData.outfit_name, isReadymadeMode, readymadeForm.name]);

  const renderCustomOutfitContent = () => {
    return (
      <>
        <div className="w-full flex justify-between items-center mb-6 bg-white py-5 px-6 ">
          <div>
            <p className="font-inter text-sm text-gray-600 max-w-2xl">
              Define a reusable template for custom order. You will set pieces, measurements, stitch
              options and outfit-level pricing in the next step.
            </p>
          </div>
          <Button
            label={editItem ? 'Update Item' : 'Save Item'}
            size="medium"
            bgColor="primary"
            leadingIcon={<FaLock size={14} />}
            onClick={handleSaveCustomOutfit}
            disabled={isLoading}
          />
        </div>

        <div className="flex gap-6 px-6">
          <div className="flex-1 max-w-[470px] rounded-md bg-white p-5">
            <div className="w-full">
              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Enter Item Name
                </Text>
                <InputField
                  type="text"
                  value={formData.outfit_name}
                  onChange={(value) => handleInputChange(value, 'outfit_name')}
                  placeholder="Enter item name"
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Select or add category
                </Text>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => handleInputChange(e.target.value, 'category')}
                    placeholder="Tap to select"
                    className="flex-1 h-10 px-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    onClick={handleOpenCategorySelector}
                    readOnly
                  />
                  {!_isEmpty(formData.category) && (
                    <Button
                      label="Change"
                      size="medium"
                      appearance="outlined"
                      onClick={handleOpenCategorySelector}
                    />
                  )}
                </div>
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Select Gender
                </Text>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleGenderChange('Male')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                      formData.gender === 'Male'
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGenderChange('Female')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                      formData.gender === 'Female'
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Select or add icons
                </Text>
                <div
                  className="w-[100px] h-[100px] border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 flex items-center justify-center relative overflow-hidden hover:border-gray-400 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={handleOpenIconSelector}
                >
                  {formData.icon ? (
                    <div className="relative w-full h-full">
                      <img
                        src={formData.icon.short_lived_url || formData.icon.url}
                        alt="Item icon"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteIcon();
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[16px] hover:bg-red-600 z-10"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="relative w-full h-full flex flex-col items-center justify-center">
                      <div className="relative mb-2">
                        <img
                          src={iconPlaceholder}
                          alt="Icon placeholder"
                          className="w-24 h-24 object-contain rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#DADADA80] rounded-lg">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  What happens next?
                </Text>
                <Text className="font-inter text-sm text-[#000000B2]" size="small" fontWeight={500}>
                  After Save, you will configure pieces, piece-wise measurements, stitch options and
                  outfit-level pricing & tax. These templates make order creation faster and reduce
                  mistakes when measuring.
                </Text>
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-[500px] flex justify-center items-start p-5">
            <div className="w-full bg-black rounded-2xl p-5 shadow-lg">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {formData.icon ? (
                    <img
                      src={formData.icon.short_lived_url || formData.icon.url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">👕</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-5 gap-2">
                    <Text
                      className="font-inter font-bold text-base text-white text-[1.1rem] line-clamp-2 leading-tight"
                      color="white"
                    >
                      {previewTitle}
                    </Text>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-white hover:text-gray-200"
                    >
                      <HiEye size={18} />
                      <Text size="medium" color="white">
                        Preview
                      </Text>
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-white mb-3">
                    <div className="flex items-center gap-1.5">
                      <img src={piece} alt="Piece" className="w-3.5 h-3.5 object-contain" />
                      <span>Single Piece</span>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <img src={wear} alt="Category" className="w-3.5 h-3.5 object-contain" />
                      <span>{formData.category}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <CategorySelectorModal
          ref={categorySelectorModalRef}
          onCategorySelect={handleCategorySelect}
          selectedCategory={formData.category}
        />
      </>
    );
  };

  const handleRemoveTag = (tagToRemove: ReadymadeTag) => {
    handleReadymadeTagsChange(readymadeForm.tags.filter((t) => t.id !== tagToRemove.id));
  };

  const renderReadymadeContent = () => {
    return (
      <>
        <div className="w-full flex justify-between items-center mb-6 bg-white py-2 px-6 ">
          <div>
            <p className="font-inter text-sm text-gray-600 max-w-2xl">
              Set pieces (optional), images, base prices, variants, barcodes, inventory and online
              visibility.
            </p>
          </div>
          <Button
            label={editItem ? 'Update' : 'Save'}
            size="medium"
            bgColor="primary"
            leadingIcon={<FaLock size={14} />}
            onClick={handleSaveReadymade}
            disabled={isLoading}
          />
        </div>

        <div className="flex gap-6 px-6">
          <div className="flex-1 max-w-[470px] rounded-md bg-white p-5">
            <div className="w-full">
              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Enter Item Name
                </Text>
                <InputField
                  type="text"
                  value={readymadeForm.name}
                  onChange={(value) => handleReadymadeInputChange('name', value)}
                  placeholder="Indo Western"
                  className="w-full"
                />
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Select or add category
                </Text>
                <button
                  type="button"
                  onClick={() => readymadeCategoryModalRef.current?.show()}
                  className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm flex items-center justify-between hover:border-gray-400"
                >
                  <span className="text-gray-600">
                    {readymadeForm.category ? readymadeForm.category.name : 'Tap to select'}
                  </span>
                  {readymadeForm.category && (
                    <span className="text-blue-600 text-xs font-semibold">Change</span>
                  )}
                </button>
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Add Tags
                </Text>
                <button
                  type="button"
                  onClick={() => readymadeTagsModalRef.current?.show()}
                  className="w-full min-h-10 py-2 px-4 border border-gray-300 rounded-lg text-sm flex items-center flex-wrap gap-2 hover:border-gray-400 text-left bg-white"
                >
                  <FaSearch className="text-gray-400 flex-shrink-0 mt-0.5" size={14} />
                  {readymadeForm.tags.length > 0 ? (
                    <>
                      {readymadeForm.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-sm font-inter"
                        >
                          <span className="truncate max-w-[180px]">{tag.name}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveTag(tag);
                            }}
                            className="p-0.5 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                            aria-label={`Remove ${tag.name}`}
                          >
                            <FaTimes size={10} />
                          </button>
                        </span>
                      ))}
                      <span className="inline-block w-px self-stretch min-h-[20px] bg-gray-300 mx-0.5" />
                    </>
                  ) : (
                    <span className="text-gray-500">Search and add tags</span>
                  )}
                </button>
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Units
                </Text>
                <button
                  type="button"
                  onClick={() => readymadeUnitsModalRef.current?.show()}
                  className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm flex items-center justify-between hover:border-gray-400"
                >
                  <span className="text-gray-600">
                    {readymadeForm.unit ? readymadeForm.unit.name : 'Pieces'}
                  </span>
                </button>
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Select Gender
                </Text>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleReadymadeGenderChange('MALE')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                      readymadeForm.gender === 'MALE'
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReadymadeGenderChange('FEMALE')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                      readymadeForm.gender === 'FEMALE'
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Female
                  </button>
                  <button
                    type="button"
                    onClick={() => handleReadymadeGenderChange('NA')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-colors ${
                      readymadeForm.gender === 'NA'
                        ? 'bg-black text-white'
                        : 'bg-white text-black border border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    N/A
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  Select or add icons
                </Text>
                <div
                  className="w-[100px] h-[100px] border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 flex items-center justify-center relative overflow-hidden hover:border-gray-400 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={handleOpenIconSelector}
                >
                  {readymadeForm.icon ? (
                    <div className="relative w-full h-full">
                      <img
                        src={readymadeForm.icon.short_lived_url || readymadeForm.icon.url}
                        alt="Item icon"
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReadymadeIconDelete();
                        }}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[16px] hover:bg-red-600 z-10"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="relative w-full h-full flex flex-col items-center justify-center">
                      <div className="relative mb-2">
                        <img
                          src={iconPlaceholder}
                          alt="Icon placeholder"
                          className="w-24 h-24 object-contain rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#DADADA80] rounded-lg">
                <Text className="font-inter font-semibold text-sm mb-2 block" color="black">
                  What happens next?
                </Text>
                <Text className="font-inter text-sm text-[#000000B2]" size="small" fontWeight={500}>
                  After saving, configure variant groups, variant matrix, pricing, inventory, taxes,
                  product description, and workflow.
                </Text>
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-[500px] flex justify-center items-start p-5">
            <div className="w-full bg-black rounded-2xl p-5 shadow-lg">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-xl bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {readymadeForm.icon ? (
                    <img
                      src={readymadeForm.icon.short_lived_url || readymadeForm.icon.url}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">👕</span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-5 gap-2">
                    <Text
                      className="font-inter font-bold text-base text-white text-[1.1rem] line-clamp-2 leading-tight"
                      color="white"
                    >
                      {previewTitle}
                    </Text>
                    <button
                      type="button"
                      className="flex items-center gap-1 text-white hover:text-gray-200"
                    >
                      <HiEye size={18} />
                      <Text size="medium" color="white">
                        Preview
                      </Text>
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-xs text-white mb-3">
                    <div className="flex items-center gap-1.5">
                      <img src={piece} alt="Piece" className="w-3.5 h-3.5 object-contain" />
                      <span>Single Piece</span>
                    </div>

                    {readymadeForm.category && (
                      <div className="flex items-center gap-1.5">
                        <img src={wear} alt="Category" className="w-3.5 h-3.5 object-contain" />
                        <span>{readymadeForm.category.name}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ReadymadeCategoryModal
          ref={readymadeCategoryModalRef}
          selectedCategory={readymadeForm.category}
          onSelect={handleReadymadeCategorySelect}
        />

        <ReadymadeTagsModal
          ref={readymadeTagsModalRef}
          selectedTags={readymadeForm.tags}
          onChange={handleReadymadeTagsChange}
        />

        <ReadymadeUnitsModal
          ref={readymadeUnitsModalRef}
          selectedUnit={readymadeForm.unit}
          onSelect={handleReadymadeUnitSelect}
        />
      </>
    );
  };

  return (
    <div className="w-full h-full bg-[#F8F8FD]  py-6 overflow-y-auto">
      <IconSelectorModal
        ref={iconSelectorModalRef}
        onIconSelect={isReadymadeMode ? handleReadymadeIconSelect : handleIconSelect}
        selectedIconId={
          isReadymadeMode ? readymadeForm.icon?.reference_id : formData.icon?.reference_id
        }
        fileType={isReadymadeMode ? '11' : '5'}
      />

      {isReadymadeMode ? renderReadymadeContent() : renderCustomOutfitContent()}
    </div>
  );
};

export default AddItem;
